import { axiosPrivate } from "@/lib/axios/axios-private";
import {
  apiErrorFromEnvelope,
  ApiError,
  formatUserErrorMessage,
} from "@/lib/api/errors";
import type {
  VideoSignedUrlData,
  VideoSignedUrlEnvelope,
} from "@/types/video-upload-types";

export type FetchVideoSignedUrlParams = {
  filename: string;
  contentType?: string;
  size: number;
  interventionId: string;
  recordedAt: string;
  videoDuration: number;
  script: string;
};

export async function fetchVideoSignedUrl(
  params: FetchVideoSignedUrlParams,
): Promise<VideoSignedUrlData> {
  const { data: envelope } = await axiosPrivate.get<VideoSignedUrlEnvelope>(
    "/api/videos/signed-url",
    {
      params: {
        filename: params.filename,
        size: params.size,
        interventionId: params.interventionId,
        recordedAt: params.recordedAt,
        videoDuration: params.videoDuration,
        script: params.script,
        ...(params.contentType ? { contentType: params.contentType } : {}),
      },
    },
  );

  if (!envelope.success || envelope.code !== 200) {
    throw apiErrorFromEnvelope(envelope, "signed-url");
  }

  const data = envelope.data ?? {};
  const signedUrl =
    data.uploadUrl?.trim() ||
    data.signedUrl?.trim() ||
    data.url?.trim() ||
    "";

  if (!signedUrl) {
    throw new ApiError(
      formatUserErrorMessage(
        "The server did not return an upload link. Try again or contact support.",
        envelope.code ?? 500,
        "signed-url",
      ),
      envelope.code ?? 500,
    );
  }

  if (!data.s3Key?.trim()) {
    throw new ApiError(
      formatUserErrorMessage(
        "The server did not return storage details for your video. Try uploading again.",
        envelope.code ?? 500,
        "signed-url",
      ),
      envelope.code ?? 500,
    );
  }

  const videoId = data.id?.trim() ?? "";
  if (!videoId) {
    throw new ApiError(
      formatUserErrorMessage(
        "The server did not return a video id. Try uploading again.",
        envelope.code ?? 500,
        "signed-url",
      ),
      envelope.code ?? 500,
    );
  }

  return {
    videoId,
    signedUrl,
    s3Key: data.s3Key.trim(),
    contentType: data.contentType?.trim() || "application/octet-stream",
    bucket: data.bucket?.trim() || "",
    videoUrl: data.videoUrl?.trim(),
  };
}

/** PUT the file to S3 using the presigned URL from the API. */
export async function uploadVideoToS3(
  signedUrl: string,
  file: File,
  contentType: string,
  onProgress?: (loaded: number, total: number) => void,
): Promise<void> {
  await new Promise<void>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", signedUrl);
    xhr.setRequestHeader("Content-Type", contentType);

    if (onProgress) {
      xhr.upload.onprogress = (event) => {
        if (!event.lengthComputable) return;
        onProgress(event.loaded, event.total);
      };
    }

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        onProgress?.(file.size, file.size);
        resolve();
        return;
      }

      reject(
        new ApiError(
          formatUserErrorMessage(
            `Storage rejected the upload for "${file.name}" (error ${xhr.status}).`,
            xhr.status || 500,
            "s3-upload",
          ),
          xhr.status || 500,
        ),
      );
    };

    xhr.onerror = () => {
      reject(
        new ApiError(
          formatUserErrorMessage(
            `Storage rejected the upload for "${file.name}" (network error).`,
            500,
            "s3-upload",
          ),
          500,
        ),
      );
    };

    xhr.send(file);
  });
}

export async function confirmVideoUpload(videoId: string): Promise<void> {
  const trimmedId = videoId.trim();
  if (!trimmedId) return;

  await axiosPrivate.post(
    `/api/videos/${encodeURIComponent(trimmedId)}/confirm-upload`,
  );
}
